import { create } from 'zustand'
import { DateTime } from 'luxon'

export interface timezoneType { value: string, offset: number, status: string }
const emptyTz: timezoneType = { value: 'na', offset: 0, status: 'inactive' }

/** Build a timezoneType representing the browser's local timezone */
const getLocalTimezone = (): timezoneType => {
    const zone = DateTime.local().zoneName || 'UTC'
    const offset = DateTime.local().offset / 60
    return { value: zone, offset, status: 'active' }
}

interface AuthState {
    user: string
    setUser: (user: string) => void
    token: string
    setToken: (token: string) => void
    userIP: string
    setUserIP: (s: string) => void
    timezone: timezoneType
    setTimezone: (t: timezoneType) => void
    /** When true, all displayed times use the browser's local timezone instead of the account timezone */
    timezoneOverride: boolean
    setTimezoneOverride: (v: boolean) => void
    toggleTimezoneOverride: () => void
    /** The effective timezone to use for display — respects the override toggle */
    effectiveTimezone: () => timezoneType
    reset: () => void
}

const useStore = create<AuthState>((set, get) => ({
    user: '',
    setUser: (user) =>
        set((state) => ({ ...state, user })),

    token: '',
    setToken: (token) =>
        set((state) => ({ ...state, token })),

    userIP: '',
    setUserIP: (userIP) =>
        set((state) => ({ ...state, userIP })),

    timezone: emptyTz,
    setTimezone: (tz: timezoneType) =>
        set((state) => ({ ...state, timezone: tz })),

    timezoneOverride: localStorage.getItem('gibi_tz_override') === 'true',
    setTimezoneOverride: (v) => {
        localStorage.setItem('gibi_tz_override', String(v))
        set((state) => ({ ...state, timezoneOverride: v }))
    },
    toggleTimezoneOverride: () => {
        const next = !get().timezoneOverride
        localStorage.setItem('gibi_tz_override', String(next))
        set((state) => ({ ...state, timezoneOverride: next }))
    },

    effectiveTimezone: () => {
        const state = get()
        if (state.timezoneOverride) return getLocalTimezone()
        // Fallback if account timezone not loaded yet
        if (!state.timezone.value || state.timezone.value === 'na') return getLocalTimezone()
        return state.timezone
    },

    reset: () =>
        set((state) => ({
            ...state,
            user: '',
            token: '',
            userIP: '',
            timezone: emptyTz,
        })),
}))

export default useStore
